import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Minus, Plus, MessageCircle, CalendarDays, ShieldCheck, Clock3, User, Phone, Mail, Building2, HeartHandshake, BadgePercent } from "lucide-react";
import Reveal from "@/components/Reveal";
import api from "@/lib/api";
import { WA_PRIMARY_LABEL, WA_SECONDARY_LABEL, buildQuoteMessage, waLink } from "@/data/site";
import { useSiteSettings, formatPhoneDisplay } from "@/lib/settings";
import { readCampaignVisit } from "@/components/CampaignBanner";
import { RESORT_OPTIONS } from "@/data/resorts";

const maskPhone = (value) => {
    const d = value.replace(/\D/g, "").slice(0, 11);
    if (d.length === 0) return "";
    if (d.length <= 2) return `(${d}`;
    if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
    if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
    return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
};

const todayISO = () => new Date().toISOString().split("T")[0];

function Stepper({ label, value, onChange, min, max, testid }) {
    return (
        <div data-testid={testid}>
            <span className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-stone-500">{label}</span>
            <div className="flex w-fit items-center gap-1 rounded-xl border border-[#E5DEC9] bg-white p-1.5">
                <button
                    type="button"
                    data-testid={`${testid}-minus`}
                    aria-label={`Diminuir ${label}`}
                    onClick={() => onChange(Math.max(min, value - 1))}
                    className="flex h-9 w-9 items-center justify-center rounded-lg text-forest-deep transition-colors hover:bg-forest-deep/5 active:scale-90"
                >
                    <Minus className="h-4 w-4" />
                </button>
                <span data-testid={`${testid}-value`} className="w-10 text-center text-sm font-semibold text-forest-deep">
                    {value}
                </span>
                <button
                    type="button"
                    data-testid={`${testid}-plus`}
                    aria-label={`Aumentar ${label}`}
                    onClick={() => onChange(Math.min(max, value + 1))}
                    className="flex h-9 w-9 items-center justify-center rounded-lg text-forest-deep transition-colors hover:bg-forest-deep/5 active:scale-90"
                >
                    <Plus className="h-4 w-4" />
                </button>
            </div>
        </div>
    );
}

const PROMISES = [
    { icon: BadgePercent, title: "Condições diferenciadas", text: "Para quem solicita sua cotação pelo portal, negociadas com cada resort parceiro." },
    { icon: HeartHandshake, title: "Atendimento humano", text: "Nada de robôs: quem responde é um especialista em resorts." },
    { icon: Clock3, title: "Resposta rápida", text: "Sua cotação chega direto no WhatsApp, sem filas e sem formulários repetidos." },
    { icon: ShieldCheck, title: "Sem compromisso", text: "Cotação gratuita. Você só avança se a proposta fizer sentido." },
];

export default function QuoteForm({
    sectionId = "cotacao-rapida",
    defaultResort = "fazzenda-park-resort",
    resort,
    onResortChange,
    eyebrow = "Cotação Personalizada",
    heading = "Solicite sua cotação",
    intro = "Cada período tem condições e disponibilidade próprias — por isso não exibimos preços automáticos. Quem cota pelo Destino Resorts recebe valores diferenciados, negociados diretamente com cada resort.",
}) {
    const [internalResort, setInternalResort] = useState(defaultResort);
    const selectedResort = resort !== undefined ? resort : internalResort;
    const setSelectedResort = onResortChange || setInternalResort;

    const [form, setForm] = useState({ firstName: "", lastName: "", phone: "", email: "", checkin: "", checkout: "", notes: "", travelReason: "" });
    const settings = useSiteSettings();
    const [adults, setAdults] = useState(2);
    const [children, setChildren] = useState(0);
    const [ages, setAges] = useState([5, 5, 5, 5, 5, 5]);

    const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

    const nights = useMemo(() => {
        if (!form.checkin || !form.checkout) return 0;
        return Math.round((new Date(form.checkout) - new Date(form.checkin)) / 86400000);
    }, [form.checkin, form.checkout]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.firstName.trim() || form.phone.replace(/\D/g, "").length < 10 || !form.checkin || !form.checkout) {
            toast.error("Preencha nome, WhatsApp e as datas previstas da viagem.");
            return;
        }
        if (form.email.trim() && !form.email.includes("@")) {
            toast.error("Confira o e-mail informado — parece incompleto.");
            return;
        }
        if (nights <= 0) {
            toast.error("A data de saída deve ser depois da data de entrada.");
            return;
        }
        const resortLabel = RESORT_OPTIONS.find((o) => o.value === selectedResort)?.label || selectedResort;
        // Abre a aba NO MOMENTO DO CLIQUE (ainda dentro do gesto do usuário).
        // Se abrir só depois do await, o navegador bloqueia como pop-up e o WhatsApp nunca abre.
        const quoteWindow = window.open("", "_blank");
        try {
            await api.post("/api/leads", {
                resort: selectedResort,
                first_name: form.firstName.trim(),
                last_name: form.lastName.trim(),
                phone: form.phone,
                email: form.email.trim(),
                checkin: form.checkin,
                checkout: form.checkout,
                nights,
                adults,
                children,
                children_ages: ages.slice(0, children),
                notes: form.notes.trim(),
                travel_reason: form.travelReason,
                campaign: readCampaignVisit(),
                origin: "formulario",
            });
        } catch {
            /* backup do lead falhou silenciosamente — o envio ao WhatsApp continua */
        }
        const message = buildQuoteMessage({ ...form, resortLabel, adults, children, ages, nights });
        const target = waLink(settings.whatsapp_primary, message);
        toast.success("Tudo certo! Abrindo o WhatsApp com sua cotação pronta.");
        if (quoteWindow) {
            quoteWindow.location.href = target;
        } else {
            window.location.href = target;
        }
    };

    return (
        <section id={sectionId} className="relative overflow-hidden bg-sand py-24 sm:py-32" data-testid="quote-section">
            <div className="pointer-events-none absolute -left-40 top-20 h-[420px] w-[420px] rounded-full bg-gold/10 blur-3xl" />
            <div className="mx-auto max-w-7xl px-4 sm:px-6">
                <div className="grid grid-cols-1 gap-12 lg:grid-cols-5 lg:gap-16">
                    <Reveal className="order-2 lg:order-1 lg:col-span-2">
                        <p className="text-xs font-semibold uppercase tracking-[0.22em] text-gold-hover">{eyebrow}</p>
                        <h2 className="mt-3 font-serif text-3xl font-semibold tracking-tight text-forest-deep sm:text-4xl">
                            {heading}
                        </h2>
                        <p className="mt-4 text-base leading-relaxed text-stone-600">{intro}</p>

                        <div className="mt-10 space-y-6">
                            {PROMISES.map((p) => (
                                <div key={p.title} className="flex items-start gap-4">
                                    <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-forest-deep text-gold">
                                        <p.icon className="h-5 w-5" />
                                    </span>
                                    <div>
                                        <h3 className="text-sm font-semibold text-forest-deep">{p.title}</h3>
                                        <p className="mt-1 text-sm leading-relaxed text-stone-600">{p.text}</p>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="mt-10 rounded-2xl border border-[#E5DEC9] bg-white/70 p-5 backdrop-blur-sm">
                            <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.16em] text-stone-500">
                                <Phone className="h-4 w-4 text-gold-hover" />
                                Prefere chamar direto?
                            </p>
                            <p className="mt-3 text-sm font-medium text-forest-deep">
                                {formatPhoneDisplay(settings.whatsapp_primary)} <span className="text-stone-400">· {WA_PRIMARY_LABEL}</span>
                            </p>
                            <p className="mt-1.5 text-sm font-medium text-forest-deep">
                                {formatPhoneDisplay(settings.whatsapp_secondary)} <span className="text-stone-400">· {WA_SECONDARY_LABEL}</span>
                            </p>
                        </div>
                    </Reveal>

                    <Reveal delay={0.15} className="order-1 lg:order-2 lg:col-span-3">
                        <form
                            onSubmit={handleSubmit}
                            noValidate
                            data-testid="quote-form"
                            className="rounded-3xl border border-[#E5DEC9] bg-white p-7 shadow-2xl shadow-forest-deep/10 sm:p-9"
                        >
                            <h3 className="font-serif text-2xl font-semibold text-forest-deep">Monte sua viagem</h3>
                            <p className="mt-1 text-sm text-stone-500">Leva menos de 1 minuto.</p>

                            <div className="mt-7 space-y-5">
                                <div>
                                    <label htmlFor="quote-resort" className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.14em] text-stone-500">
                                        <Building2 className="h-3.5 w-3.5" /> Resort desejado *
                                    </label>
                                    <select
                                        id="quote-resort"
                                        data-testid="quote-input-resort"
                                        value={selectedResort}
                                        onChange={(e) => setSelectedResort(e.target.value)}
                                        className="field-input"
                                    >
                                        {RESORT_OPTIONS.map((o) => (
                                            <option key={o.value} value={o.value}>
                                                {o.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label htmlFor="quote-checkin" className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-stone-500">
                                            Entrada prevista *
                                        </label>
                                        <input
                                            id="quote-checkin"
                                            data-testid="quote-input-checkin"
                                            type="date"
                                            min={todayISO()}
                                            value={form.checkin}
                                            onChange={set("checkin")}
                                            className="field-input"
                                        />
                                    </div>
                                    <div>
                                        <label htmlFor="quote-checkout" className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-stone-500">
                                            Saída prevista *
                                        </label>
                                        <input
                                            id="quote-checkout"
                                            data-testid="quote-input-checkout"
                                            type="date"
                                            min={form.checkin || todayISO()}
                                            value={form.checkout}
                                            onChange={set("checkout")}
                                            className="field-input"
                                        />
                                    </div>
                                </div>

                                {nights > 0 && (
                                    <p data-testid="quote-nights-badge" className="inline-flex items-center gap-2 rounded-full bg-forest-deep/5 px-4 py-1.5 text-xs font-semibold text-forest-deep">
                                        <CalendarDays className="h-3.5 w-3.5 text-gold-hover" />
                                        {nights} {nights === 1 ? "diária" : "diárias"} selecionadas
                                    </p>
                                )}

                                <div className="flex flex-wrap gap-6">
                                    <Stepper label="Adultos (13+)" value={adults} onChange={setAdults} min={1} max={10} testid="quote-input-adults" />
                                    <Stepper label="Crianças (0 a 12)" value={children} onChange={setChildren} min={0} max={6} testid="quote-input-children" />
                                </div>

                                {children > 0 && (
                                    <div data-testid="quote-input-children-ages">
                                        <span className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-stone-500">
                                            Idade das crianças (anos)
                                        </span>
                                        <div className="grid grid-cols-3 gap-3 sm:grid-cols-6">
                                            {Array.from({ length: children }).map((_, i) => (
                                                <select
                                                    key={i}
                                                    data-testid={`quote-input-child-age-${i}`}
                                                    aria-label={`Idade da criança ${i + 1}`}
                                                    value={ages[i]}
                                                    onChange={(e) => setAges((a) => a.map((v, idx) => (idx === i ? Number(e.target.value) : v)))}
                                                    className="field-input px-2 py-2.5 text-center"
                                                >
                                                    {Array.from({ length: 13 }).map((_, age) => (
                                                        <option key={age} value={age}>
                                                            {age} {age === 1 ? "ano" : "anos"}
                                                        </option>
                                                    ))}
                                                </select>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                                    <div>
                                        <label htmlFor="quote-firstname" className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.14em] text-stone-500">
                                            <User className="h-3.5 w-3.5" /> Nome *
                                        </label>
                                        <input
                                            id="quote-firstname"
                                            data-testid="quote-input-firstname"
                                            type="text"
                                            value={form.firstName}
                                            onChange={set("firstName")}
                                            placeholder="Seu nome"
                                            className="field-input"
                                            autoComplete="given-name"
                                        />
                                    </div>
                                    <div>
                                        <label htmlFor="quote-lastname" className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-stone-500">
                                            Sobrenome
                                        </label>
                                        <input
                                            id="quote-lastname"
                                            data-testid="quote-input-lastname"
                                            type="text"
                                            value={form.lastName}
                                            onChange={set("lastName")}
                                            placeholder="Seu sobrenome"
                                            className="field-input"
                                            autoComplete="family-name"
                                        />
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                                    <div>
                                        <label htmlFor="quote-phone" className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.14em] text-stone-500">
                                            <Phone className="h-3.5 w-3.5" /> WhatsApp com DDD *
                                        </label>
                                        <input
                                            id="quote-phone"
                                            data-testid="quote-input-phone"
                                            type="tel"
                                            inputMode="numeric"
                                            value={form.phone}
                                            onChange={(e) => setForm((f) => ({ ...f, phone: maskPhone(e.target.value) }))}
                                            placeholder="(47) 99999-9999"
                                            className="field-input"
                                            autoComplete="tel"
                                        />
                                    </div>
                                    <div>
                                        <label htmlFor="quote-email" className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.14em] text-stone-500">
                                            <Mail className="h-3.5 w-3.5" /> Seu melhor e-mail
                                        </label>
                                        <input
                                            id="quote-email"
                                            data-testid="quote-input-email"
                                            type="email"
                                            value={form.email}
                                            onChange={set("email")}
                                            placeholder="voce@email.com"
                                            className="field-input"
                                            autoComplete="email"
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label htmlFor="quote-reason" className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-stone-500">
                                        Motivo da viagem <span className="font-normal normal-case tracking-normal text-stone-400">(opcional)</span>
                                    </label>
                                    <select
                                        id="quote-reason"
                                        data-testid="quote-input-reason"
                                        value={form.travelReason}
                                        onChange={set("travelReason")}
                                        className="field-input"
                                    >
                                        <option value="">Selecione, se quiser nos contar</option>
                                        <option value="Férias em família">Férias em família</option>
                                        <option value="Viagem a dois / romântica">Viagem a dois / romântica</option>
                                        <option value="Comemoração especial">Comemoração especial</option>
                                        <option value="Descanso e bem-estar">Descanso e bem-estar</option>
                                        <option value="Aventura e natureza">Aventura e natureza</option>
                                        <option value="Evento ou grupo">Evento ou grupo</option>
                                        <option value="Outro">Outro</option>
                                    </select>
                                </div>

                                <div>
                                    <label htmlFor="quote-notes" className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-stone-500">
                                        Observações (ocasião especial, regime preferido...)
                                    </label>
                                    <textarea
                                        id="quote-notes"
                                        data-testid="quote-input-notes"
                                        value={form.notes}
                                        onChange={set("notes")}
                                        rows={3}
                                        placeholder="Aniversário, lua de mel, férias em família, evento da empresa..."
                                        className="field-input resize-none"
                                    />
                                </div>

                                <button
                                    type="submit"
                                    data-testid="quote-form-submit-button"
                                    className="group flex w-full items-center justify-center gap-2.5 rounded-full bg-wa px-8 py-4 text-sm font-semibold text-white shadow-xl shadow-emerald-900/25 transition-all duration-200 hover:-translate-y-px hover:bg-wa-dark active:scale-[0.98]"
                                >
                                    <MessageCircle className="h-5 w-5 transition-transform duration-300 group-hover:-rotate-12" />
                                    Enviar e receber cotação no WhatsApp
                                </button>
                                <p className="text-center text-[11px] leading-relaxed text-stone-400">
                                    Ao enviar, o WhatsApp abre com sua mensagem pronta para {formatPhoneDisplay(settings.whatsapp_primary)}. Cotação gratuita e sem compromisso.
                                </p>
                            </div>
                        </form>
                    </Reveal>
                </div>
            </div>
        </section>
    );
}
